import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { ReactComponent as EyeIcon } from 'assets/icon-visible.svg';
import AuthHeader from 'shared/components/layout/AuthHeader';
import Footer from 'shared/components/layout/Footer';
import { Button } from 'shared/components/Buttons';
import { logos } from 'shared/defaultTheme';
import AlertModal from 'shared/components/AlertModal';
import MainSpinner from 'shared/components/MainSpinner';
import Breadcrumb from 'shared/components/Breadcrumb';

import useApi from 'hooks/useApi';
import useLanguages from 'hooks/useLanguages';
import { addBreadcrumb, removeBreadcrumb } from 'redux/actions/layoutActions';

import { changeTheme } from 'redux/actions/themeActions';
import { convertErrorsArrayToString } from 'shared/helpers/helper';
import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS } from 'shared/utils';
import PreviewNotification from 'shared/components/PreviewNotification';
import { PREVIEW_NOTIFICATION } from 'shared/constants/variants';
import ContentCard from './components/ContentCard';
import Title from './components/Title';
import BrandGraphicAspectionSection from './components/BrandGraphicAspectSection';
import SocialNetworksFooterSection from './components/SocialNetworksFooterSection';
import CustomEmailNotification from './components/CustomEmailNotification';
import SendEmailSettingsSection from './components/SendEmailSettingsSection';
import ModalPreview from './components/ModalPreview';
import PreviewExternalSite from './components/PreviewExternalSite';
import LanguagesSelector from './components/LanguagesSelector';
import ConfigSyncClientLink from './components/ConfigSyncClientLink';
import SaveDocumentsConfig from './components/SaveDocumentsConfig';

const GeneralCustomizer = () => {
  const { t } = useTranslation();
  const theme = useSelector((state) => state.theme);
  const auth = useSelector((state) => state.auth);
  const topBar = useSelector((state) => state.topBar);
  const history = useHistory();
  const params = useParams();
  const dispatch = useDispatch();
  const { customAreaApi } = useApi();
  const { breadcrumbs } = useSelector((state) => state.layout);
  const [selectedMainColor, setSelectedMainColor] = useState();
  const [selectedSecondaryColor, setSelectedSecondaryColor] = useState();
  const [selectedPageHeaderOption, setSelectedPageHeaderOption] = useState();
  const [selectedPageFooterOption, setSelectedPageFooterOption] = useState();
  const [selectedLogoBase4, setSelectedLogoBase4] = useState();
  const [selectedLogoFooterBase4, setSelectedLogoFooterBase4] = useState();
  const [selectedFavicoBase4, setSelectedFavicoBase4] = useState();
  const [selectedNotificationImg, setSelectedNotificationImg] = useState();
  const [showLogoFooter, setShowLogoFooter] = useState(true);
  const [showNotifications, setShowNotification] = useState(false);
  const [footerText, setFooterText] = useState('');
  const [youtubeLink, setYoutubeLink] = useState('');
  const [twitterLink, setTwitterLink] = useState('');
  const [facebookLink, setFacebookLink] = useState('');
  const [linkedinLink, setLinkedinLink] = useState('');
  const [phone, setPhone] = useState('');
  const [tabTitle, setTabTitle] = useState('');
  const [socialReason, setSocialReason] = useState('');
  const [isGenericAccount, setIsGenericAccount] = useState(false);
  const [isGenericAccountSet, setIsGenericAccountSet] = useState(false);
  const [userGeneric, setUserGeneric] = useState('');
  const [server, setServer] = useState('');
  const [port, setPort] = useState('');
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [safeConnection, setSafeConnection] = useState(true);
  const [TLS, setTLS] = useState(true);
  const [openPreviewNotification, setOpenPreviewNotification] = useState(false);
  const [openPreviewExternalSite, setOpenPreviewExternalSite] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertType, setAlertType] = useState(ALERT_TYPE_SUCCESS);
  const [alertMessage, setAlertMessage] = useState('');
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [selectedDefaultLanguage, setSelectedDefaultLanguage] = useState('es-ES');
  const [configMails, setConfigMails] = useState([]);
  const [hasCLientLink, setHasClientLink] = useState(false);
  const [selectedConfigOption, setSelectedConfigOption] = useState();
  const [saveDocsComunications, setSaveDocsComunications] = useState(false);
  const [admins, setAdmins] = useState([]);
  const { languages } = useLanguages();

  const [{ loading: loadingCusConfig }, getConfig] = customAreaApi(
    {
      url: `${window.API_V1}/cus/customers/${auth.cusNavId}`,
      method: 'GET',
    },
    { manual: true }
  );

  const [{ loading: loadingConfig }, modifyConfig] = customAreaApi(
    {
      url: `${window.API_V1}/cus/customers/${auth.cusNavId}/modify/configuration`,
      method: 'POST',
    },
    { manual: true }
  );

  const [{ loading: loadingSmtp }, modifySmtp] = customAreaApi(
    {
      url: `${window.API_V1}/cus/customers/${auth.cusNavId}/modify/smtp`,
      method: 'POST',
    },
    { manual: true }
  );

  const [{ loading: loadingLang }, modifyLang] = customAreaApi(
    {
      url: `${window.API_V1}/cus/langs/${auth.cusNavId}`,
      method: 'POST',
    },
    { manual: true }
  );

  const [{ loading: loadingAdmins }, getAdmins] = customAreaApi(
    {
      url: `${window.API_V1}/cli/contacts/${auth.cusNavId}/admins?adminActive=false`,
      method: 'GET',
    },
    { manual: true }
  );

  const [{ loading: modifyAdminsLoading }, modifyAdmins] = customAreaApi(
    {
      url: `${window.API_V1}/cli/contacts/${auth.cusNavId}/admins/Modify`,
      method: 'PUT',
    },
    { manual: true }
  );

  useEffect(() => {
    if (auth.admins.length > 0) {
      setAdmins(auth.admins.filter((x) => x.active));
    }

    (async () => {
      const {
        data: { errors: loadingAdminsError, data },
      } = await getAdmins();
      if (loadingAdminsError) {
        console.error('error getting customer config');
      }
      setAdmins(data);
    })();
  }, []);

  useEffect(() => {
    if (breadcrumbs.length > 1) {
      breadcrumbs.forEach(() => {
        dispatch(removeBreadcrumb());
      });
      dispatch(
        addBreadcrumb({
          active: false,
          label: 'general_home',
          link: '/home',
          isModify: false,
        })
      );
    }

    dispatch(
      addBreadcrumb({
        label: 'general_customizer_title',
        active: true,
        link: '',
        isModify: false,
      })
    );
  }, []);

  const loadConfig = async () => {
    const {
      data: { errors: loadingConfigErrors, data },
    } = await getConfig();
    if (loadingConfigErrors.length) {
      console.error('error getting customer config');
      return;
    }
    const { config, smtp, relations } = data;
    const existClientLink = relations.some((x) => x === 'clientlink');
    setHasClientLink(existClientLink);
    setConfigMails(config.mails || []);
    setSelectedMainColor(config.colorPpal);
    setSelectedSecondaryColor(config.colorSec);
    setSelectedPageHeaderOption(config.header);
    setSelectedConfigOption(config.syncContacts);
    setSelectedPageFooterOption(config.footer);
    setSelectedLogoBase4(config.logo);
    setSelectedLogoFooterBase4(config.whiteLogo);
    setSelectedFavicoBase4(config.favicon);
    setSelectedNotificationImg(config.logoNotify);
    setShowLogoFooter(config.showLogoFooter ?? false);
    setShowNotification(config.showNotifications ?? false);
    setFooterText(config.info);
    setYoutubeLink(config.linkYoutube);
    setTwitterLink(config.linkTwitter);
    setFacebookLink(config.linkFacebook);
    setLinkedinLink(config.linkLinkedin);
    setPhone(config.phone);
    setTabTitle(config.title);
    setSocialReason(config.socialReason);
    setSaveDocsComunications(config.saveDocsComunications);
    if (smtp && (smtp.server || smtp.userGeneric)) {
      setIsGenericAccount(smtp.isGenericAccount);
      setServer(smtp.server || '');
      setPort(smtp.port || '');
      setUser(smtp.user || '');
      setPass(smtp.pass || '');
      setSafeConnection(smtp.ssl || '');
      setTLS(smtp.tls || '');
      if (smtp.userGeneric) {
        setUserGeneric(smtp.userGeneric.split('@')[0]);
        setIsGenericAccountSet(true);
      } else {
        setUserGeneric('');
      }
    }
  };

  useEffect(() => {
    setSelectedLanguages(languages);
    setSelectedDefaultLanguage(topBar.selectedDefaultLanguage);
  }, [languages, topBar.selectedDefaultLanguage]);

  useEffect(() => {
    const { action } = params;
    if (action === 'edit') {
      loadConfig();
    }
  }, [params]);

  const onToggleModalPreviewNotification = () =>
    setOpenPreviewNotification(!openPreviewNotification);

  const onToggleModalPreviewExternalSite = () =>
    setOpenPreviewExternalSite(!openPreviewExternalSite);

  const showAlert = (type, message) => {
    setAlertType(type);
    setAlertMessage(message);
    setOpenAlert(true);
  };

  const closeAlert = () => {
    if (alertType === ALERT_TYPE_ERROR) {
      setAlertType(ALERT_TYPE_SUCCESS);
      setAlertMessage('');
      setOpenAlert(false);
      return;
    }
    history.push('/home');
  };

  const validateRequiredData = () => {
    const errors = [];
    if (!selectedDefaultLanguage || selectedDefaultLanguage.length === 0) {
      errors.push(t('general_customizer_default_language_required'));
    }
    if (selectedLanguages.length <= 0) {
      errors.push(t('general_customizer_language_required'));
    }
    if (!selectedLogoBase4) {
      errors.push(t('general_customizer_logo_required'));
    }
    if (!selectedLogoFooterBase4) {
      errors.push(t('general_customizer_white_logo_required'));
    }
    if (!selectedFavicoBase4) {
      errors.push(t('general_customizer_favicon_requried'));
    }
    if (!selectedMainColor) {
      errors.push(t('general_customizer_main_color_required'));
    }
    if (!selectedSecondaryColor) {
      errors.push(t('general_customizer_secondary_color_required'));
    }
    if (!selectedNotificationImg) {
      errors.push(t('general_customizer_notification_image_required'));
    }
    if (isGenericAccount) {
      if (!userGeneric) {
        errors.push(t('general_customizer_user_required'));
      }
    } else {
      if (!server) {
        errors.push(t('general_customizer_outbound_server_required'));
      }
      if (!port) {
        errors.push(t('general_customizer_port_required'));
      }
      if (!user) {
        errors.push(t('general_customizer_user_required'));
      }
      if (!pass) {
        errors.push(t('general_customizer_password_required'));
      }
    }

    return errors;
  };

  const getSmtpData = () => {
    return {
      server,
      port,
      user,
      account: user,
      pass,
      ssl: safeConnection,
      tls: TLS,
      userGeneric,
      isGenericAccount,
    };
  };

  const onClean = () => {
    setSelectedMainColor(undefined);
    setSelectedSecondaryColor(undefined);
    setSelectedPageHeaderOption(undefined);
    setSelectedConfigOption(undefined);
    setSelectedPageFooterOption(undefined);
    setSelectedLogoBase4(undefined);
    setSelectedLogoFooterBase4(undefined);
    setSelectedNotificationImg(undefined);
    setShowLogoFooter(true);
    setShowNotification(false);
    setFooterText('');
    setYoutubeLink('');
    setTwitterLink('');
    setFacebookLink('');
    setLinkedinLink('');
    setPhone('');
    setTabTitle('');
    setSocialReason('');
    setIsGenericAccount(false);
    setUserGeneric('');
    setServer('');
    setPort('');
    setUser('');
    setPass('');
    setSafeConnection(true);
    setTLS(true);
    setSelectedLanguages([]);
    setSelectedDefaultLanguage('');
  };

  const onSave = async () => {
    try {
      const validationErrors = validateRequiredData();
      if (validationErrors.length) {
        showAlert(ALERT_TYPE_ERROR, validationErrors.join(', '));
        return;
      }
      const configData = {
        logo: selectedLogoBase4,
        whiteLogo: selectedLogoFooterBase4,
        logoFooter: selectedLogoFooterBase4,
        logoNotify: selectedNotificationImg,
        favicon: selectedFavicoBase4,
        colorPpal: selectedMainColor,
        colorSec: selectedSecondaryColor,
        header: selectedPageHeaderOption,
        syncContacts: selectedConfigOption,
        footer: selectedPageFooterOption,
        phone,
        info: footerText,
        linkYoutube: youtubeLink,
        linkTwitter: twitterLink,
        linkFacebook: facebookLink,
        linkLinkedin: linkedinLink,
        showLogoFooter,
        showNotifications,
        title: tabTitle,
        admins,
        socialReason,
        saveDocsComunications,
      };
      const {
        data: { errors: configErrors },
      } = await modifyConfig({
        data: configData,
      });
      if (configErrors.length) {
        throw new Error('error throw by modify config');
      }
      const langData = selectedLanguages.map((language) => ({
        id: language.value,
        name: language.name,
        default: language.value === selectedDefaultLanguage,
      }));
      const {
        data: { errors: langErrors },
      } = await modifyLang({
        data: langData,
      });
      if (langErrors.length) {
        throw new Error('error throw by modify lang: ', convertErrorsArrayToString(langErrors));
      }
      const smtpData = getSmtpData();
      const {
        data: { errors: smtpErrors },
      } = await modifySmtp({
        data: smtpData,
      });
      if (smtpErrors.length) {
        throw new Error('error throw by modify smtp');
      }
      const adminsData = admins.map((admin) => ({
        ...admin,
        name: admin.nameComplete,
      }));
      const {
        data: { errors: modifyAdminsErrors },
      } = await modifyAdmins({
        data: adminsData,
      });
      if (modifyAdminsErrors.length) {
        throw new Error('error throw by modify admins');
      }
      const favIconEl = document.getElementById('favicon');
      favIconEl.href = selectedFavicoBase4;
      dispatch(changeTheme(configData));
      showAlert(ALERT_TYPE_SUCCESS, t('general_customizer_success_message'));
    } catch (ex) {
      console.error('saving customer config: ', ex);
      const code = ex?.response?.data.errors[0]?.code;
      switch (code) {
        case 'CA-04':
          showAlert(ALERT_TYPE_ERROR, t('document_file_size_large'));
          break;
        case 'CA-051':
          showAlert(ALERT_TYPE_ERROR, t('general_customizer_smtp_account_invalid_email'));
          break;
        case 'CA-052':
          showAlert(ALERT_TYPE_ERROR, t('general_customizer_smtp_account_invalid_email_chars'));
          break;
        case 'CA-053':
          showAlert(ALERT_TYPE_ERROR, t('general_customizer_smtp_account_already_exists'));
          break;
        default:
          showAlert(ALERT_TYPE_ERROR, t('general_error_message'));
      }
    }
  };

  const onCancel = () => {
    history.goBack();
    if (breadcrumbs.length > 1) {
      breadcrumbs.forEach(() => {
        dispatch(removeBreadcrumb());
      });
      dispatch(
        addBreadcrumb({
          active: false,
          label: 'general_home',
          link: '/home',
          isModify: false,
        })
      );
    }
  };

  const { mainColor, logoHeader } = theme;
  return (
    <div className="general-customizer">
      <AuthHeader
        className="general-customizer__header"
        logoSrc={logoHeader ?? logos.lefebvreWhite}
        backgroundColor={theme.backgroundHeader}
        withBottomBar={false}
      />
      <div className="general-customizer__container">
        <div className="general-customizer__container__breadcrumb">
          <Breadcrumb />
        </div>
        <ContentCard>
          <Title color={mainColor} />
          <div className="general-customizer_content">
            <Subtitle className="general-customizer_content_subtitle" color={mainColor}>
              {t('general_customizer_desc')}
            </Subtitle>
            <LanguagesSelector
              mainColor={mainColor}
              selectedLanguages={selectedLanguages}
              setSelectedLanguages={setSelectedLanguages}
              selectedDefaultLanguage={selectedDefaultLanguage}
              setSelectedDefaultLanguage={setSelectedDefaultLanguage}
            />
            <BrandGraphicAspectionSection
              mainColor={mainColor}
              selectedLogoBase4={selectedLogoBase4}
              setSelectedLogoBase4={setSelectedLogoBase4}
              selectedMainColor={selectedMainColor}
              setSelectedMainColor={setSelectedMainColor}
              selectedSecondaryColor={selectedSecondaryColor}
              setSelectedSecondaryColor={setSelectedSecondaryColor}
              selectedPageHeaderOption={selectedPageHeaderOption}
              setSelectedPageHeaderOption={setSelectedPageHeaderOption}
              selectedPageFooterOption={selectedPageFooterOption}
              setSelectedPageFooterOption={setSelectedPageFooterOption}
              selectedLogoFooterBase4={selectedLogoFooterBase4}
              setSelectedLogoFooterBase4={setSelectedLogoFooterBase4}
              selectedFavicoBase4={selectedFavicoBase4}
              setSelectedFavicoBase4={setSelectedFavicoBase4}
              tabTitle={tabTitle}
              setTabTitle={setTabTitle}
              socialReason={socialReason}
              setSocialReason={setSocialReason}
            />
            <SocialNetworksFooterSection
              mainColor={mainColor}
              showLogoFooter={showLogoFooter}
              setShowLogoFooter={setShowLogoFooter}
              youtubeLink={youtubeLink}
              setYoutubeLink={setYoutubeLink}
              twitterLink={twitterLink}
              setTwitterLink={setTwitterLink}
              facebookLink={facebookLink}
              setFacebookLink={setFacebookLink}
              linkedinLink={linkedinLink}
              setLinkedinLink={setLinkedinLink}
              footerText={footerText}
              setFooterText={setFooterText}
              phone={phone}
              setPhone={setPhone}
            />
            <CustomEmailNotification
              mainColor={mainColor}
              configMails={configMails}
              admins={admins}
              setAdmins={setAdmins}
            />
            <SendEmailSettingsSection
              mainColor={mainColor}
              selectedNotificationImg={selectedNotificationImg}
              setSelectedNotificationImg={setSelectedNotificationImg}
              isGenericAccount={isGenericAccount}
              setIsGenericAccount={setIsGenericAccount}
              isGenericAccountSet={isGenericAccountSet}
              userGeneric={userGeneric}
              setUserGeneric={setUserGeneric}
              server={server}
              setServer={setServer}
              port={port}
              setPort={setPort}
              user={user}
              setUser={setUser}
              pass={pass}
              setPass={setPass}
              safeConnection={safeConnection}
              setSafeConnection={setSafeConnection}
              TLS={TLS}
              setTLS={setTLS}
            />
            <ConfigSyncClientLink
              mainColor={mainColor}
              selectedConfigOption={selectedConfigOption}
              setSelectedConfigOption={setSelectedConfigOption}
              hasCLientLink={hasCLientLink}
            />
            <SaveDocumentsConfig
              mainColor={mainColor}
              saveDocsComunications={saveDocsComunications}
              setSaveDocsComunications={setSaveDocsComunications}
            />
          </div>
          <Row className="general-customizer__footer_content general-customizer__footer_content-row">
            <Col
              xl={6}
              lg={6}
              className="d-flex flex-row justify-content-preview align-items-center"
            >
              <Button
                className="general-customizer__button-preview"
                label={t('general_customizer_button_notification_preview')}
                color="secondary"
                type="button"
                onClick={onToggleModalPreviewNotification}
                icon={<EyeIcon fill={mainColor} />}
                textColor={mainColor}
              />
              <Button
                className="general-customizer__button-preview"
                label={t('general_customizer_button_external_page_preview')}
                color="secondary"
                type="button"
                onClick={onToggleModalPreviewExternalSite}
                icon={<EyeIcon fill={mainColor} />}
                textColor={mainColor}
              />
            </Col>
            <Col
              xl={6}
              lg={6}
              className="d-flex flex-row justify-content-action align-items-center"
            >
              <Button
                className="general-customizer__footer-button general-customizer__footer-button--space"
                label={t('general_button_cancel')}
                color="secondary"
                type="button"
                onClick={() => onCancel()}
                textColor={mainColor}
              />
              <Button
                className="general-customizer__footer-button general-customizer__footer-button--space"
                label={t('general_button_clean')}
                color="secondary"
                type="button"
                onClick={onClean}
                textColor={mainColor}
              />
              <Button
                className="general-customizer__footer-button"
                label={t('general_button_save')}
                color="primary"
                type="button"
                onClick={onSave}
                backgroundColor={mainColor}
              />
            </Col>
          </Row>
          <ModalPreview isOpen={openPreviewNotification} toggle={onToggleModalPreviewNotification}>
            <PreviewNotification
              theme={theme}
              variant={PREVIEW_NOTIFICATION.CUSTOMIZER}
              logoHeader={selectedLogoBase4}
              mainColor={selectedMainColor}
              logoFooter={selectedLogoFooterBase4}
              footerText={footerText}
              phone={phone}
              logoNotify={selectedNotificationImg}
              selectedPageHeaderOption={selectedPageHeaderOption}
              selectedPageFooterOption={selectedPageFooterOption}
            />
          </ModalPreview>
          <ModalPreview isOpen={openPreviewExternalSite} toggle={onToggleModalPreviewExternalSite}>
            <PreviewExternalSite
              theme={theme}
              logo={selectedLogoBase4}
              mainColor={selectedMainColor}
              secondaryColor={selectedSecondaryColor}
              headerOption={selectedPageHeaderOption}
              footerOption={selectedPageFooterOption}
              whiteLogo={selectedLogoFooterBase4}
              footerText={footerText}
              phone={phone}
              showLogoFooter={showLogoFooter}
            />
          </ModalPreview>
        </ContentCard>
      </div>
      <Footer
        logoSrc={theme.logoFooter}
        backgroundColor={theme.backgroundFooter}
        footerText={theme.footerText}
        iconColor={theme.footerIconColor}
        textColor={theme.footerTextColor}
        facebookLink={theme.facebookLink}
        linkedinLink={theme.linkedinLink}
        youtubeLink={theme.youtubeLink}
        twitterLink={theme.twitterLink}
        phone={theme.phone}
        showLogoFooter={theme.showLogoFooter}
      />
      <AlertModal
        type={alertType}
        isOpen={openAlert}
        toggle={closeAlert}
        message={alertMessage}
        okText={t('general_button_ok')}
        onOk={closeAlert}
      />
      {(loadingCusConfig ||
        loadingConfig ||
        loadingSmtp ||
        loadingLang ||
        loadingAdmins ||
        modifyAdminsLoading) && <MainSpinner />}
    </div>
  );
};

const Subtitle = styled.h1(({ color }) => ({
  color,
}));

export default GeneralCustomizer;
